import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, PlayCircle, Award, TrendingUp, Clock, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import { MOCK_COURSES } from '../utils/mockData';

function UserDashboard() {
  const [myCourses, setMyCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchMyCourses();
  }, []);

  const fetchMyCourses = async () => {
    // Simulate loading delay
    setTimeout(() => {
      // Mock: Return 2 courses for enrolled users
      setMyCourses(MOCK_COURSES.slice(0, 2));
      setLoading(false);
    }, 500);
  };

  return (
    <div className="min-h-screen pt-32 pb-24 bg-stone-50" data-testid="user-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-2">Welcome back, {user.name}!</h1>
          <p className="text-lg text-slate-600">Continue your learning journey</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="stat-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-extrabold text-slate-900">{myCourses.length}</div>
                <div className="text-slate-600 mt-1">Enrolled Courses</div>
              </div>
              <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center">
                <BookOpen className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl p-8 border border-amber-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-extrabold text-slate-900">0</div>
                <div className="text-slate-600 mt-1">Certificates Earned</div>
              </div>
              <div className="w-14 h-14 bg-amber-500 rounded-xl flex items-center justify-center">
                <Award className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 border border-green-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-extrabold text-slate-900">0%</div>
                <div className="text-slate-600 mt-1">Avg. Completion</div>
              </div>
              <div className="w-14 h-14 bg-green-600 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* My Courses */}
        <section>
          <h2 className="text-3xl font-extrabold text-slate-900 mb-8">My Courses</h2>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100 shimmer h-80" />
              ))}
            </div>
          ) : myCourses.length === 0 ? (
            <div className="bg-white rounded-3xl p-16 border border-slate-100 text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <BookOpen className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">No courses yet</h3>
              <p className="text-slate-600 mb-6">Start your learning journey by enrolling in a course</p>
              <Link to="/courses" data-testid="browse-courses-link">
                <button className="btn-primary">Browse Courses</button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myCourses.map((course, index) => (
                <MyCourseCard key={course.id} course={course} index={index} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function MyCourseCard({ course, index }) {
  const [progress, setProgress] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchProgress();
    // eslint-disable-next-line
  }, []);

  const fetchProgress = async () => {
    // Simulate loading and return mock progress
    setTimeout(() => {
      const completedLessons = Math.floor(Math.random() * (course.lessons?.length || 0));
      const totalLessons = course.lessons?.length || 0;
      const percentage = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

      setProgress({
        completed_lessons: completedLessons,
        total_lessons: totalLessons,
        percentage: percentage
      });
    }, 300);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
    >
      <Link to={`/course-player/${course.id}`} data-testid={`my-course-card-${course.id}`}>
        <div className="course-card h-full flex flex-col">
          <div className="relative overflow-hidden">
            <img
              src={course.thumbnail}
              alt={course.title}
              className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute top-4 left-4 bg-blue-600 text-white rounded-full px-3 py-1 text-sm font-semibold">
              {progress ? `${progress.percentage}%` : '0%'}
            </div>
          </div>
          <div className="p-6 flex-grow flex flex-col">
            <h3 className="text-xl font-bold text-slate-900 mb-2 line-clamp-2">{course.title}</h3>
            <div className="flex items-center gap-2 text-sm text-slate-600 mb-4">
              <Clock className="w-4 h-4" />
              <span>{course.lessons?.length || 0} lessons</span>
            </div>
            {progress && (
              <div className="mt-auto">
                <div className="w-full bg-slate-200 rounded-full h-2 mb-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress.percentage}%` }}
                  />
                </div>
                <p className="text-sm text-slate-600">
                  {progress.completed_lessons} of {progress.total_lessons} lessons completed
                </p>
              </div>
            )}
            <button className="mt-4 w-full bg-blue-600 text-white py-3 rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
              <PlayCircle className="w-5 h-5" />
              Continue Learning
            </button>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default UserDashboard;
